import {Paper, Switch} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import {useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import {chartPalette} from "./chartUtils";


const maxLvl = 71;
let levels = [];
for (let i = 1; i <= maxLvl; i+=5) {
    levels.push(i);
}

const prob = [1,2,3,4,5,6];
const forceSeries = prob.map((p, i) => ({name: `${p}%`, color: chartPalette[i]}));


const forceChance = (prob, lvl, cumulative) => {
    if(cumulative && lvl > 1) {
        let notNow = 1 - forceChance(prob, lvl, false);
        let notCum = 1 - forceChance(prob, lvl-5, true);
        return 1 - (notCum*notNow);
    } else {
        return (prob + ((lvl - 1) / 5) * (prob / 2))/100;
    }
}


const forceLevels = [];
for (let i = 1; i <= 200; i++) {
    forceLevels.push(i);
}
const calculateAlpha = (level) => {
    return Math.atan2(Math.exp(level/25), 20)/Math.PI+0.5;
}
const calculateFPRegen = (level, alpha) => {
    return Math.max(Math.round((level*alpha)/4.7), 1);
}


const chartAlpha = forceLevels.map(l => {
    const alpha = calculateAlpha(l);
    return {level: l, Alpha: alpha * 100, Regen: calculateFPRegen(l, alpha)};
});
const chartAlphaSeries = [
    {name: 'Alpha', color: chartPalette[0]},
    {name: 'Regen', color: chartPalette[1], yAxisId: 'right'},
];


const perc = new Intl.NumberFormat(undefined, {style: 'percent', maximumFractionDigits: 2})

export default function Force () {

    const [cumulative, setCumulative] = useState(false);


    const chartForce = useMemo(() => levels.map(l => {
        const row = {level: l};
        prob.forEach(p => { row[`${p}%`] = forceChance(p, l, cumulative) * 100; });
        return row;
    }), [cumulative]);

    return (
        <div className="w-full">
            <title>Force Charts</title>
            <Row align="center">
                <Col><h1>Force</h1></Col>
                <Col grow={1} />
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?The_Force" target="_blank" rel="noreferrer"><Icon icon={IconMap.SwcRules} /> Full rules</a></Col>
            </Row>
            <Paper className="mb-3">
                <div className="float-right flex flex-row">
                    <label htmlFor="cumulative" className="mr-1">Cumulative</label>
                    <Switch id="cumulative" checked={cumulative} onChange={e => setCumulative(e.currentTarget.checked)}/>
                </div>
                <h2 className="mt-0">Force Chance</h2>

                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th/>
                                <th colSpan={prob.length + 1}>Force Probability</th>
                            </tr>
                            <tr>
                                <th>Level</th>
                                {prob.map(s => <th key={s}>{s}%</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {levels.map(lvl => (
                                <tr key={lvl}>
                                    <th>{lvl}</th>
                                    {prob.map(p => <td key={p} className="text-right">{perc.format(forceChance(p, lvl, cumulative))}</td>)}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} data={chartForce} series={forceSeries} dataKey="level" withLegend curveType="linear" yAxisLabel="%" xAxisLabel="Level" />
                    </Col>
                </Row>
            </Paper>
            <Paper>
                <h2 className="mt-0">Force Alpha</h2>

                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th>Force Level</th>
                                <th>Alpha</th>
                                <th>FP Regen/h</th>
                            </tr>
                            </thead>
                            <tbody>
                            {forceLevels.map(lvl => {
                                let a = calculateAlpha(lvl);
                                let fp = calculateFPRegen(lvl, a);
                                return (<tr key={lvl}>
                                    <th>{lvl}</th>
                                    <td>{perc.format(a)}</td>
                                    <td className="text-center">{fp}</td>
                                </tr>);
                            })}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart
                            h={300}
                            data={chartAlpha}
                            series={chartAlphaSeries}
                            dataKey="level"
                            withLegend
                            withDots={false}
                            curveType="linear"
                            withRightYAxis
                            yAxisLabel="%"
                            rightYAxisLabel="FP / h"
                            xAxisLabel="Force Level"
                        />
                    </Col>
                </Row>

            </Paper>
        </div>
    )
}
